import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Animated, Clipboard, Image, StyleSheet, Text, Pressable, View } from 'react-native';
import TextUtils from '../../../textUtils';
import TextInput from './text_input_plain';
import ValidatedComponent from './validated_comp';
import { __WEB__, COLOR, THEME } from '../../../constants/styles';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const FormInput = forwardRef((props, ref) => {
  // consumers currently set value instead of default value
  // so keep supporting that, but also they should switch over to defaultValue
  // when they want to use this as an un-controlled component
  // if they explicitly set controlled param to true then we will use value instead
  const defaultValueRef = useRef(props.defaultValue != null ? props.defaultValue : props.value);
  const [isCopied, setIsCopied] = useState(false);
  const copyButtonBackground = useRef(new Animated.Value(0)).current;
  const copyResetTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const validatedRef = useRef(null);

  const focus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const validate = useCallback(() => {
    return validatedRef.current?.validate();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus,
      validate,
    }),
    [focus, validate]
  );

  const clearCopyFeedbackTimeout = useCallback(() => {
    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearCopyFeedbackTimeout();
    };
  }, [clearCopyFeedbackTimeout]);

  const setCopyFeedbackAnimation = useCallback(
    (toValue, onComplete) => {
      Animated.timing(copyButtonBackground, {
        toValue,
        duration: 180,
        useNativeDriver: false,
      }).start(({ finished }) => {
        if (finished) onComplete?.();
      });
    },
    [copyButtonBackground]
  );

  const showCopyFeedback = useCallback(() => {
    clearCopyFeedbackTimeout();
    setIsCopied(true);
    setCopyFeedbackAnimation(1);

    copyResetTimeoutRef.current = setTimeout(() => {
      setCopyFeedbackAnimation(0, () => {
        setIsCopied(false);
      });
      copyResetTimeoutRef.current = null;
    }, 500);
  }, [clearCopyFeedbackTimeout, setCopyFeedbackAnimation]);

  const getDisplayValue = useCallback(() => {
    const { controlled, sanitizer, value } = props;
    const isControlled = controlled === true || !!sanitizer;

    if (isControlled) return value == null ? '' : value;
    return defaultValueRef.current;
  }, [props.controlled, props.sanitizer, props.value]);

  const handleChangeText = useCallback(
    (text) => {
      props.onChangeText(props.sanitizer?.(text, true) || text);
    },
    [props.onChangeText, props.sanitizer]
  );

  const handleCopyPress = useCallback(() => {
    const textToCopy = getDisplayValue();
    if (!textToCopy) return;

    if (__WEB__) navigator.clipboard.writeText(textToCopy);
    else Clipboard.setString(textToCopy);

    showCopyFeedback();
  }, [getDisplayValue, showCopyFeedback]);

  let {
    label,
    editable = true,
    readOnly = false,
    maxLength,
    inputContainerStyle,
    controlled,
    value,
    containerStyle,
    sanitizer,
  } = props;

  controlled = controlled === true || !!sanitizer;

  // we mostly dont need our textinput to be a controlled component
  // but if controlled is set true this enables the value to be controlled by the value prop
  // consumers should start using defaultValue instead of value when they dont want a controlled
  // component
  // for a controlled component we have to set the value prop, and leave the defaultValue
  // undefined. For an uncontrolled component we have to set the defaultValue prop and leave
  // the value prop undefined (per the react-native TextInput component)
  // so init them both to undefined, and unless controlled is explicitly set true we assume
  // its uncontrolled
  let valueValue;
  let defaultValue;
  if (!controlled) {
    defaultValue = defaultValueRef.current;
  } else {
    valueValue = value === null ? '' : value;
  }
  const displayValue = valueValue || defaultValue;
  const canCopy =
    displayValue !== null &&
    displayValue !== undefined &&
    displayValue !== '' &&
    props.hasCopyButton;
  const copyButtonTestID = `${props.testID || label}-copy-button`;
  const copyButtonBackgroundColor = copyButtonBackground.interpolate({
    inputRange: [0, 1],
    outputRange: [COLOR.button, COLOR.textDisabled],
  });

  let { keyboardType, returnKeyType } = props;
  keyboardType = keyboardType || 'default';
  returnKeyType = __WEB__ ? 'done' : returnKeyType || 'next';

  const autoCorrect = !(props.autoCorrect === false);

  // Cast string "true"/"false" to true/false. Try to be permissive and also
  // cast other garbage values to false.
  if (typeof editable !== 'boolean') editable = editable === 'true';

  readOnly = readOnly || !editable;

  return (
    <ValidatedComponent ref={validatedRef} {...props} style={[styles.container, containerStyle]}>
      <View
        style={[
          styles.inputContainer,
          props.disabled ? styles.inputContainerDisabled : null,
          inputContainerStyle,
        ]}
      >
        {!!readOnly ? (
          <Text
            style={[THEME.input, styles.readOnlyText, styles.content]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {displayValue}
          </Text>
        ) : (
          <TextInput
            ref={inputRef}
            style={[
              styles.input,
              styles.content,
              props.multiline ? styles.inputMultiline : null,
              props.inputStyle,
            ]}
            autoFocus={props.autoFocus || false}
            onBlur={props.onBlur}
            value={valueValue}
            defaultValue={defaultValue}
            maxLength={maxLength}
            multiline={props.multiline}
            numberOfLines={props.numberOfLines}
            placeholder={TextUtils.ac(label)}
            placeholderTextColor={COLOR.textLight}
            autoCorrect={autoCorrect}
            autoComplete={__WEB__ ? props.label : null}
            autoCapitalize={props.autoCapitalize || 'words'}
            keyboardType={keyboardType}
            returnKeyType={returnKeyType}
            selectTextOnFocus={props.selectTextOnFocus !== false}
            clearButtonMode="while-editing"
            underlineColorAndroid="transparent"
            onChangeText={handleChangeText}
            onSubmitEditing={props.onSubmitEditing}
            testID={props.testID || label}
          />
        )}
        {canCopy ? (
          <AnimatedPressable
            onPress={handleCopyPress}
            style={[
              styles.copyBtn,
              { backgroundColor: copyButtonBackgroundColor, opacity: props.disabled ? 0.5 : 1 },
            ]}
            testID={copyButtonTestID}
          >
            <Image
              source={require('../../../../resources/icon-copy.png')}
              style={styles.copyBtnIcon}
            />
            <Text style={styles.copyBtnLabel}>{isCopied ? 'Copied' : 'Copy'}</Text>
          </AnimatedPressable>
        ) : null}
      </View>
    </ValidatedComponent>
  );
});

export default FormInput;

const styles = StyleSheet.create({
  container: {},
  placeholderTextContainer: {
    position: 'absolute',
    top: 12,
    left: 0,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: COLOR.text,
    marginBottom: 10,
    marginTop: 2,
  },
  inputContainerDisabled: {
    ...THEME.inputContainerDisabled,
  },
  input: {
    ...THEME.input,
  },
  content: {
    flex: 1,
  },
  inputMultiline: {
    height: 80,
    paddingTop: 12,
  },
  readOnlyText: {
    paddingTop: 12,
  },
  copyBtn: {
    marginBottom: 6,
    paddingHorizontal: 10,
    height: 40,
    width: 110,
    backgroundColor: COLOR.button,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  copyBtnIcon: {
    height: 20,
    width: 20,
    marginRight: 6,
  },
  copyBtnLabel: {
    ...THEME.textRegular,
    color: COLOR.accent,
    flex: 1,
    textAlign: 'center',
  },
});
